import React, { useEffect, useState, useCallback } from 'react';
import { Button, Card, Nav, Modal, Form } from 'react-bootstrap';
import { database } from '../firebase';
import { ref, onValue, push } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { Line } from 'react-chartjs-2';
import { Link, useLocation } from 'react-router-dom';
import 'chartjs-adapter-date-fns';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

import type { TooltipDataAttrs } from 'react-calendar-heatmap';

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Count {
  id: string;
  timestamp: number;
}

interface HeatmapValue {
  date: string; 
  count: number;
}

const VeGa = () => {
  const [counts, setCounts] = useState<Count[]>([]);
  const [totalCounts, setTotalCounts] = useState(0);
  const [rolloverDate, setRolloverDate] = useState('');
  const [veloGaPrice, setVeloGaPrice] = useState('');
  const [chartData, setChartData] = useState<any>(null); 
  const [activityChartData, setActivityChartData] = useState<HeatmapValue[]>([]); 
  const [countsSinceRollover, setCountsSinceRollover] = useState(0);
  const [showRidesModal, setShowRidesModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedRidesCount, setSelectedRidesCount] = useState<number>(0);
  const { currentUser } = useAuth();
  const location = useLocation();
  const [showAddRideModal, setShowAddRideModal] = useState(false);
  const [customRideDateTime, setCustomRideDateTime] = useState<string>('');
  const [addRideError, setAddRideError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
    const dateRef = ref(database, `vega/${currentUser.uid}/rollover_date`);
    const priceRef = ref(database, `vega/${currentUser.uid}/velo_ga_price`);

    const unsubscribeDate = onValue(dateRef, (snapshot) => {
      const date = snapshot.val();
      if (date) {
        setRolloverDate(date);
      }
    });

    const unsubscribePrice = onValue(priceRef, (snapshot) => {
      const price = snapshot.val();
      if (price) {
        setVeloGaPrice(price.toString());
      }
    });

    const unsubscribeCounts = onValue(countsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const countsArray = Object.entries(data).map(([id, timestamp]) => ({
        id,
        timestamp: timestamp as number
      })).sort((a, b) => b.timestamp - a.timestamp);
      
      setCounts(countsArray);
      setTotalCounts(countsArray.length);
    });

    return () => {
      unsubscribeCounts();
      unsubscribeDate();
      unsubscribePrice();
    };
  }, [currentUser]);

  useEffect(() => {
    if (rolloverDate && counts.length > 0) {
      const rolloverTimestamp = Math.floor(new Date(rolloverDate).getTime() / 1000);
      const countsSince = counts.filter(count => count.timestamp >= rolloverTimestamp).length;
      setCountsSinceRollover(countsSince);
    } else {
      setCountsSinceRollover(0);
    }
  }, [counts, rolloverDate]);

  const incrementCounter = async () => {
    if (!currentUser) return;
    
    try {
      const timestamp = Math.floor(Date.now() / 1000);
      const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
      await push(countsRef, timestamp);
    } catch (error) {
      console.error('Error updating counter:', error);
    }
  };

  // Helpers for custom date/time modal
  const formatDateTimeLocal = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const openAddRideModal = () => {
    const now = new Date();
    setCustomRideDateTime(formatDateTimeLocal(now));
    setAddRideError(null);
    setShowAddRideModal(true);
  };

  const handleAddCustomRide = async () => {
    if (!currentUser) return;
    try {
      if (!customRideDateTime) {
        setAddRideError('Bitte Datum und Uhrzeit wählen.');
        return;
      }
      const dt = new Date(customRideDateTime);
      if (isNaN(dt.getTime())) {
        setAddRideError('Ungültiges Datum/Zeit.');
        return;
      }
      const timestamp = Math.floor(dt.getTime() / 1000);
      const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
      await push(countsRef, timestamp);
      setShowAddRideModal(false);
    } catch (error) {
      console.error('Error adding custom ride:', error);
      setAddRideError('Fehler beim Speichern.');
    }
  };

  const generateChartData = () => {
    if (!rolloverDate || !veloGaPrice) return null;

    const price = parseInt(veloGaPrice);
    const sortedCounts = [...counts].sort((a, b) => a.timestamp - b.timestamp);

    const data = [];
    const trendData = [];
    const labels = [];
    
    const startDate = new Date(rolloverDate);
    const endDate = new Date(startDate);
    endDate.setFullYear(startDate.getFullYear() + 1);

    labels.push(startDate);
    data.push({ x: startDate, y: price });
    trendData.push({ x: startDate, y: price });

    let currentCount = 0;
    sortedCounts.forEach((count) => {
      const date = new Date(count.timestamp * 1000);
      if (date >= startDate) {
        currentCount++;
        labels.push(date);
        data.push({ x: date, y: price - (currentCount * 7) });
        
        const daysSinceStart = Math.floor((date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const trendValue = price - (price * (daysSinceStart / totalDays));
        trendData.push({ x: date, y: trendValue });
      }
    });

    const lastDate = sortedCounts.length > 0 
      ? new Date(Math.max(startDate.getTime(), sortedCounts[sortedCounts.length - 1].timestamp * 1000))
      : startDate;
        
    for (let date = new Date(lastDate); date <= endDate; date.setDate(date.getDate() + 7)) {
      const currentDate = new Date(date);
      labels.push(currentDate);
      data.push({ x: currentDate, y: null }); 
        
      const daysSinceStart = Math.floor((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const trendValue = Math.max(0, price - (price * (daysSinceStart / totalDays)));
      trendData.push({ x: currentDate, y: trendValue });
    }

    trendData.push({ x: endDate, y: 0 });

    return {
      labels,
      datasets: [
        {
          label: 'Remaining Price',
          data: data,
          borderColor: '#1780a6',
          backgroundColor: 'rgba(23, 128, 166, 0.1)',
          fill: true,
          tension: 0.1,
        },
        {
          label: 'Trend to Zero',
          data: trendData,
          borderColor: '#ff6b6b',
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          tension: 0,
        }
      ]
    };
  };

  useEffect(() => {
    if (rolloverDate && veloGaPrice) {
      setChartData(generateChartData());
    }
  }, [counts, rolloverDate, veloGaPrice]);

  const generateActivityChartData = useCallback(() => {
    if (counts.length === 0) {
      return [];
    }

    const dailyData: { [key: string]: number } = {};
    counts.forEach(count => {
      const date = new Date(count.timestamp * 1000);
      const dateKey = date.toISOString().split('T')[0]; 
      dailyData[dateKey] = (dailyData[dateKey] || 0) + 1;
    });

    const heatmapValues: HeatmapValue[] = [];
    
    // Calculate start and end dates
    const endDate = new Date();
    const startDate = new Date();
    startDate.setFullYear(endDate.getFullYear() - 1);
    
    // Generate data for each day in the range
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      heatmapValues.push({
        date: dateKey,
        count: dailyData[dateKey] || 0,
      });
    }
    return heatmapValues;
  }, [counts]); 

  useEffect(() => {
    setActivityChartData(generateActivityChartData());
  }, [counts, generateActivityChartData]); 

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Price (CHF)'
        }
      },
      x: {
        type: 'time' as const,
        time: {
          unit: 'month' as const,
          displayFormats: {
            month: 'MMM yyyy'
          }
        },
        title: {
          display: true,
          text: 'Date'
        },
        min: rolloverDate,
        max: rolloverDate ? new Date(new Date(rolloverDate).setFullYear(new Date(rolloverDate).getFullYear() + 1)).toISOString() : undefined
      }
    }
  };

  const getTooltipDataAttrs = (value: any) => {
    if (!value || !value.date || typeof value.count !== 'number' || value.count === 0) {
      return { 'data-tip': '' } as any as TooltipDataAttrs; 
    } else {
      return { 'data-tip': `${value.date}: ${value.count} rides` } as any as TooltipDataAttrs;
    }
  };

  const getClassForValue = (value: any) => {
    if (!value || !value.date || typeof value.count !== 'number' || value.count === 0) {
      return 'color-github-0'; 
    }
    if (value.count >= 4) {
      return 'color-github-4';
    }
    return `color-github-${value.count}`; 
  };

  // Calculate the start and end dates for the heatmap
  // Using a different approach to ensure proper alignment
  const today = new Date();
  const oneYearAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 1));

  if (!currentUser) {
    return (
      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>VeGa Counter</Card.Title>
          <p>Please log in to use the counter.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="container">
      <h2 className="mb-4">VeGa</h2>
      
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega" 
            active={location.pathname === '/dashboard/vega'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega/rides" 
            active={location.pathname === '/dashboard/vega/rides'}
          >
            Fahrten
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega/settings" 
            active={location.pathname === '/dashboard/vega/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <div className="row mb-4">
        <div className="col-md-8">
          <Card className="h-100">
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>VeGa Counter</Card.Title>
              <div className="d-flex flex-column">
                <h4 className="mt-2">Counts since {rolloverDate}: {countsSinceRollover}</h4>
                <h4 className="mb-0">Total Counts: {totalCounts}</h4>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="h-100">
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Add Count</Card.Title>
              <Button onClick={openAddRideModal} className="w-100">Increment</Button>
            </Card.Body>
          </Card>
        </div>
      </div>

      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>Price Burndown Chart</Card.Title>
          {chartData && <Line data={chartData} options={chartOptions} />}
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Body>
          <Card.Title style={{ color: 'rgb(75, 192, 192)' }}>Activity Chart</Card.Title>
          {activityChartData.length > 0 ? (
            <div className="clickable-heatmap"> 
              <CalendarHeatmap
                startDate={new Date(new Date().setDate(new Date().getDate() - 357))} 
                endDate={new Date(new Date().setDate(new Date().getDate() + 7))}    
                values={activityChartData}
                classForValue={getClassForValue}
                tooltipDataAttrs={getTooltipDataAttrs}
                showMonthLabels={true}
                showWeekdayLabels={true}
                weekdayLabels={['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']}
                horizontal={true}
                gutterSize={5}
                onClick={(value) => {
                  if (value && value.date) {
                    setSelectedDate(value.date);
                    setSelectedRidesCount(value.count);
                    setShowRidesModal(true);
                  }
                }}
              />
            </div>
          ) : (
            <p>No activity data to display.</p>
          )}
        </Card.Body>
      </Card>
      
      {/* Modal for displaying rides for selected date */}
      <Modal show={showRidesModal} onHide={() => setShowRidesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>VeGa Rides for {selectedDate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="rides-details">
            <h4>Date: {selectedDate}</h4>
            <h5>Total Rides: {selectedRidesCount}</h5>
            
            {selectedRidesCount > 0 ? (
              <div className="mt-3">
                <p>You recorded {selectedRidesCount} environmentally friendly commute{selectedRidesCount !== 1 ? 's' : ''} on this day.</p>
                {selectedRidesCount >= 2 && (
                  <p className="text-success">Great job using eco-friendly transportation!</p>
                )}
              </div>
            ) : (
              <p>No VeGa rides recorded for this date.</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRidesModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={incrementCounter}
            disabled={selectedDate !== new Date().toISOString().split('T')[0]}
          >
            Add Ride for Today
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for adding a ride with custom date & time */}
      <Modal show={showAddRideModal} onHide={() => setShowAddRideModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>VeGa-Fahrt erfassen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Datum und Uhrzeit</Form.Label>
              <Form.Control
                type="datetime-local"
                value={customRideDateTime}
                onChange={(e) => setCustomRideDateTime(e.target.value)}
              />
              {addRideError && (
                <div className="text-danger small mt-2">{addRideError}</div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddRideModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddCustomRide} disabled={!customRideDateTime}>
            Ride hinzufügen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VeGa;
